import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Mail } from 'lucide-react';
import FormField from '@/shared/ui/FormField';
import { primaryButtonClass } from '@/shared/ui/formClasses';
import { Spinner } from '@/shared/ui/spinner';
import { Checkbox } from '@/shared/ui/checkbox';
import {
  loginErrors,
  type LoginErrors,
  type LoginForm as LoginFormValues,
} from '../model/credentials';
import PasswordField from './PasswordField';

type LoginFormProps = {
  defaultEmail: string;
  defaultRememberMe: boolean;
  submitting: boolean;
  onSubmit: (form: LoginFormValues) => void;
};

/** `onSubmit({ email, password, rememberMe })`; l'email e "Ricordami" partono dai valori salvati. */
export default function LoginForm({
  defaultEmail,
  defaultRememberMe,
  submitting,
  onSubmit,
}: LoginFormProps) {
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(defaultRememberMe);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<LoginErrors>({});

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const found = loginErrors({ email, password });
    setErrors(found);
    if (Object.keys(found).length === 0) onSubmit({ email, password, rememberMe });
  };

  return (
    <form noValidate onSubmit={handleSubmit} className="space-y-5">
      <FormField
        id="login-email"
        label="Email o username"
        required
        autoComplete="username"
        placeholder="nome@azienda.it"
        value={email}
        onChange={setEmail}
        error={errors.email}
        trailing={<Mail className="mr-2 h-3.5 w-3.5 text-placeholder" aria-hidden />}
      />
      <PasswordField
        id="login-password"
        label="Password"
        required
        autoComplete="current-password"
        placeholder="••••••••"
        value={password}
        onChange={setPassword}
        error={errors.password}
        visible={showPassword}
        onToggleVisible={() => setShowPassword((visible) => !visible)}
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Checkbox
          checked={rememberMe}
          onCheckedChange={(checked) => setRememberMe(checked === true)}
        >
          Ricordami
        </Checkbox>
        <Link
          to="/forgot-password"
          className="rounded-xs text-sm font-medium text-link hover:underline focus-visible:focus-ring"
        >
          Password dimenticata?
        </Link>
      </div>

      <button type="submit" disabled={submitting} className={`${primaryButtonClass} w-full`}>
        {submitting ? (
          <>
            <Spinner size="xs" tone="current" label={null} />
            Accesso in corso…
          </>
        ) : (
          'Accedi'
        )}
      </button>
    </form>
  );
}
